import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search, Building2, Users, Megaphone, Loader2, Target, Handshake } from "lucide-react";
import { globalAdminSearch } from "@/lib/admin-search.functions";
import { useAgenciasFilters } from "@/contexts/agencias-filters";
import { emptyAgenciasFilters } from "@/components/admin/AgenciasFiltersSheet";

import { cn } from "@/lib/utils";
import { useEntityPeek } from "@/components/admin/entity-peek/EntityPeekContext";
import type { EntityKind } from "@/components/admin/entity-peek/entity-registry";

export function AdminGlobalSearch() {
  const navigate = useNavigate();
  const search = useServerFn(globalAdminSearch);
  const { setFilters: setAgenciasFilters } = useAgenciasFilters();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const enabled = debounced.length >= 2;
  const { data, isFetching, isError } = useQuery({
    queryKey: ["admin-search", debounced],
    queryFn: () => search({ data: { q: debounced } }),
    enabled,
    staleTime: 30_000,
    // Al seguir escribiendo se muestran los resultados anteriores hasta que
    // llegan los nuevos, en vez de un "Buscando…" en cada pulsación.
    placeholderData: keepPreviousData,
    retry: false,
    throwOnError: false,
  });

  const peek = useEntityPeek();
  // Abre el resultado en el panel lateral, sin salir de la pantalla actual.
  const go = (kind: EntityKind, id: string) => {
    setOpen(false);
    setQ("");
    if (peek) peek.open({ kind, id });
    else if (kind === "agencia")
      navigate({ to: "/admin/agencias", search: { page: 1, pageSize: 50, open: id } });
    else if (kind === "contacto")
      navigate({ to: "/admin/personas", search: { page: 1, pageSize: 50, open: id } });
    else if (kind === "anuncio")
      navigate({ to: "/admin/anuncios", search: { page: 1, pageSize: 100, open: id } });
  };

  // Abre el listado de agencias filtrado por la búsqueda (allí se puede ordenar).
  const verTodasAgencias = () => {
    setOpen(false);
    setAgenciasFilters({ ...emptyAgenciasFilters, nombre: debounced });
    setQ("");
    navigate({ to: "/admin/agencias", search: { page: 1, pageSize: 50 } });
  };

  const total =
    (data?.agencias.length ?? 0) +
    (data?.contactos.length ?? 0) +
    (data?.anuncios.length ?? 0) +
    (data?.demandas.length ?? 0) +
    (data?.operaciones.length ?? 0);

  return (
    <div ref={wrapRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Buscar agencias, contactos, anuncios, demandas…"
          className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && enabled && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {!data && isFetching && (
            <div className="p-4 text-sm text-muted-foreground">Buscando…</div>
          )}
          {!data && isError && (
            <div className="p-4 text-sm text-destructive">
              No se pudo buscar. Inténtalo de nuevo.
            </div>
          )}
          {data && total === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Sin resultados.</div>
          )}
          {data && data.agencias.length > 0 && (
            <Group title="Agencias" icon={Building2}>
              {data.agencias.map((a) => (
                <Item
                  key={a.id}
                  onClick={() => go("agencia", a.id)}
                  title={a.nombre}
                  subtitle={[
                    a.region,
                    `${a.anuncios_count ?? 0} anuncio${a.anuncios_count === 1 ? "" : "s"}`,
                    a.url_web,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              ))}
              {data.agencias_total > data.agencias.length && (
                <button
                  type="button"
                  onClick={verTodasAgencias}
                  className="block w-full px-3 py-2 text-left text-xs font-medium text-primary hover:bg-accent"
                >
                  Ver más resultados en Agencias ({data.agencias_total} coincidencias) →
                </button>
              )}
            </Group>
          )}
          {data && data.contactos.length > 0 && (
            <Group title="Contactos" icon={Users}>
              {data.contactos.map((c) => (
                <Item
                  key={c.id}
                  onClick={() => go("contacto", c.id)}
                  title={[c.nombre, c.apellidos].filter(Boolean).join(" ")}
                  subtitle={[c.email, c.telefono, c.ciudad].filter(Boolean).join(" · ")}
                />
              ))}
            </Group>
          )}
          {data && data.anuncios.length > 0 && (
            <Group title="Anuncios" icon={Megaphone}>
              {data.anuncios.map((a) => (
                <Item
                  key={a.id}
                  onClick={() => go("anuncio", a.id)}
                  title={a.alias || a.referencia || "—"}
                  subtitle={[
                    a.referencia,
                    a.idealista_id ? `Idealista #${a.idealista_id}` : null,
                    a.direccion_completa
                      ? a.direccion_completa
                      : [a.calle, a.numero].filter(Boolean).join(" "),
                    a.cp,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              ))}
            </Group>
          )}
          {data && data.demandas.length > 0 && (
            <Group title="Demandas" icon={Target}>
              {data.demandas.map((d) => (
                <Item
                  key={d.id}
                  onClick={() => go("demanda", d.id)}
                  title={d.titulo || d.referencia || "—"}
                  subtitle={[d.referencia, d.estado].filter(Boolean).join(" · ")}
                />
              ))}
            </Group>
          )}
          {data && data.operaciones.length > 0 && (
            <Group title="Operaciones" icon={Handshake}>
              {data.operaciones.map((o) => (
                <Item
                  key={o.id}
                  onClick={() => go("operacion", o.id)}
                  title={o.titulo || o.referencia || "—"}
                  subtitle={[o.referencia, o.estado].filter(Boolean).join(" · ")}
                />
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Search;
  children: React.ReactNode;
}) {
  return (
    <div className="py-1">
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
      {children}
    </div>
  );
}

function Item({
  title,
  subtitle,
  onClick,
}: {
  title: string;
  subtitle?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("block w-full px-3 py-2 text-left text-sm hover:bg-accent")}
    >
      <div className="truncate font-medium text-foreground">{title || "—"}</div>
      {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
    </button>
  );
}
